import { API_URLS } from "../config";
import { apiRequest } from "./apiRequest";

// Fallback used until the API responds (or if it fails)
export const DEFAULT_CURRENCY_RATES = { INR: 1, USD: 94.51, AED: 25.73, QAR: 25.96, SAR: 25.20, SGD: 73.71, EUR: 103.15, GBP: 121.94, OMR: 245.62, AUD: 61.50 };

// Returns a { CODE: rate } map — server values merged over defaults
export const fetchCurrencyRates = async (token) => {
  const result = await apiRequest(API_URLS.FINANCE_CURRENCY_RATES, {}, token);
  if (result?.ok && result.data?.rates) {
    return { ...DEFAULT_CURRENCY_RATES, ...result.data.rates, INR: 1 };
  }
  return { ...DEFAULT_CURRENCY_RATES };
};

// Loads the full dated rate history, grouped by currency code (ascending by date) —
// for looking up a currency's rate as of a past date (e.g. an invoice's Invoice Date)
// without re-fetching per lookup. Pair with rateFromHistoryAsOf()/ratesAsOf() below.
// Same shape/source as the backend's getAllCurrencyRateHistory().
export const fetchCurrencyRateHistory = async (token) => {
  const result = await apiRequest(API_URLS.FINANCE_CURRENCY_RATES, {}, token);
  const byCode = {};
  (result?.data?.rows || []).forEach(r => {
    if (!byCode[r.code]) byCode[r.code] = [];
    byCode[r.code].push({ date: r.date, rate: r.rate });
  });
  Object.values(byCode).forEach(entries => entries.sort((a, b) => new Date(a.date) - new Date(b.date)));
  return byCode;
};

// The rate for `code` as of `dateStr` from a history map built by
// fetchCurrencyRateHistory() — the most recent dated entry on or before that date,
// falling back to DEFAULT_CURRENCY_RATES, or null for an unrecognized currency code.
export const rateFromHistoryAsOf = (history, code, dateStr) => {
  if (!code || code === 'INR') return 1;
  const entries = history[code];
  const asOfTime = new Date(dateStr).getTime();
  if (!entries?.length || isNaN(asOfTime)) return DEFAULT_CURRENCY_RATES[code] ?? null;
  let best = null;
  for (const e of entries) { if (new Date(e.date).getTime() <= asOfTime) best = e.rate; else break; }
  return best != null ? best : (DEFAULT_CURRENCY_RATES[code] ?? null);
};

// The full { CODE: rate } map as of `dateStr` — the date-aware counterpart to
// fetchCurrencyRates()'s "as of now" map, recomputed locally from a history loaded
// once via fetchCurrencyRateHistory() with no extra network round trip.
export const ratesAsOf = (history, dateStr) => {
  const codes = new Set([...Object.keys(DEFAULT_CURRENCY_RATES), ...Object.keys(history)]);
  const map = {};
  codes.forEach(code => { map[code] = rateFromHistoryAsOf(history, code, dateStr) ?? DEFAULT_CURRENCY_RATES[code] ?? 1; });
  map.INR = 1;
  return map;
};
